import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Smartphone, Sparkles, Gem } from 'lucide-react';
import { cn } from '@/lib/utils';
import BenefitSlot from './BenefitSlot';
import type { Redemption } from './types';

// Import benefit images
import vidrioImg from '@/assets/benefits/vidrio-temperado.png';
import estucheImg from '@/assets/benefits/estuche.png';
import limpiezaImg from '@/assets/benefits/limpieza.png';
import hidrogelImg from '@/assets/benefits/hidrogel.png';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Shield,
  Smartphone,
  Sparkles,
  Gem,
};

const imageMap: Record<string, string> = {
  vidrio_temperado: vidrioImg,
  estuche: estucheImg,
  limpieza: limpiezaImg,
  hidrogel: hidrogelImg,
};

export interface PremiumBenefitCardProps {
  benefitKey: string;
  name: string;
  maxQuantity: number;
  usedQuantity: number;
  description: string;
  icon: string;
  iconBg: string;
  redemptions?: Redemption[];
  isAdmin?: boolean;
  onRedeem?: (benefitKey: string) => void;
}

const PremiumBenefitCard: React.FC<PremiumBenefitCardProps> = ({
  benefitKey,
  name,
  maxQuantity,
  usedQuantity,
  description,
  icon,
  iconBg,
  redemptions = [],
  isAdmin = false,
  onRedeem,
}) => {
  const IconComponent = iconMap[icon] || Shield;
  const productImage = imageMap[benefitKey];
  const isFullyRedeemed = usedQuantity >= maxQuantity;
  const canRedeem = isAdmin && !isFullyRedeemed && onRedeem;

  // Get redemption dates for this benefit this month
  const currentMonth = new Date().toISOString().slice(0, 7);
  const thisMonthRedemptions = redemptions
    .filter(r => r.benefit_type === benefitKey && r.month_year === currentMonth)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  const handleSlotClick = () => {
    if (canRedeem && onRedeem) {
      onRedeem(benefitKey);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className={cn(
        'relative overflow-hidden rounded-2xl transition-all duration-300 group',
        'border border-white/10 bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-xl',
        isFullyRedeemed && 'ring-2 ring-green-500/30'
      )}
    >
      {/* Product Image Section */}
      <div className="relative h-36 overflow-hidden">
        {productImage ? (
          <>
            <img 
              src={productImage} 
              alt={name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          </>
        ) : (
          <div className={cn('w-full h-full bg-gradient-to-br', iconBg)} />
        )}

        {/* Shimmer effect on hover */}
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        {/* Floating icon badge */}
        <motion.div 
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          className={cn(
            'absolute top-3 right-3 w-10 h-10 rounded-xl flex items-center justify-center',
            'bg-gradient-to-br shadow-lg backdrop-blur-sm',
            iconBg
          )}
        >
          <IconComponent className="w-5 h-5 text-white drop-shadow-lg" />
        </motion.div>

        {/* Completion badge */}
        {isFullyRedeemed && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-3 left-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg"
          >
            ✓ Completo
          </motion.div>
        )}

        {/* Benefit name on image */}
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-white font-bold text-lg drop-shadow-lg">
            {maxQuantity}x {name}
          </h3>
          <p className="text-white/70 text-sm drop-shadow">{description}</p>
        </div>
      </div>

      {/* Slots Section */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white/60 text-sm font-medium">
            Estado de beneficios
          </span>
          <span className={cn(
            'text-sm font-bold',
            isFullyRedeemed ? 'text-green-400' : 'text-blue-400'
          )}>
            {usedQuantity}/{maxQuantity}
          </span>
        </div>

        {/* Benefit slots grid */}
        <div className={cn(
          'grid gap-2',
          maxQuantity === 1 ? 'grid-cols-1' : 
          maxQuantity === 2 ? 'grid-cols-2' : 
          'grid-cols-3'
        )}>
          {Array.from({ length: maxQuantity }).map((_, i) => {
            const isUsed = i < usedQuantity;
            const redemption = thisMonthRedemptions[i];
            
            return (
              <BenefitSlot
                key={i}
                index={i}
                isUsed={isUsed}
                usedDate={redemption?.created_at}
                redeemerName={redemption?.redeemer_name}
                redeemerUbicacion={redemption?.redeemer_ubicacion}
                isAdmin={isAdmin}
                canRedeem={canRedeem && !isUsed}
                onClick={handleSlotClick}
              />
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${(usedQuantity / maxQuantity) * 100}%` }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className={cn(
              'h-full rounded-full',
              isFullyRedeemed 
                ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
                : 'bg-gradient-to-r from-blue-400 to-blue-500'
            )}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default PremiumBenefitCard;
